import React from 'react';
import { Link } from 'react-router-dom';
import { appConfig } from '../../config/appConfig';

/**
 * Contextual CTA Component
 * 
 * Provides varied call-to-action messages based on context to reduce repetition
 * and improve user experience by matching CTAs to the content that precedes them.
 */

const ContextualCTA = ({ variant = 'default', className = '' }) => {
  const variants = {
    // After about/team sections - focus on learning more
    about: {
      badge: 'Work With Us',
      title: 'Ready to Meet the Team?',
      description: "Let's discuss how our experience can help you achieve your exit goals.",
      primaryCTA: {
        text: 'Schedule a Consultation',
        link: appConfig.links.calendly,
        external: true
      },
      secondaryCTA: {
        text: 'View Our Process',
        link: '/process',
        external: false
      }
    },

    // After services/challenges sections - focus on solutions
    services: {
      badge: 'Get Started',
      title: 'Let Us Help You Navigate These Challenges',
      description: 'Get a free valuation and discover how we can maximize your exit value.',
      primaryCTA: {
        text: 'Get Free Valuation',
        link: '/valuation',
        external: false
      },
      secondaryCTA: {
        text: 'Talk to an Advisor',
        link: appConfig.links.calendly,
        external: true
      }
    },

    // After valuation/analysis sections - focus on next steps
    valuation: {
      badge: 'Next Steps',
      title: 'Ready to Discover Your Business Value?',
      description: 'Get instant insights with our AI-powered valuation tool, then talk to an expert.',
      primaryCTA: {
        text: 'Start Free Valuation',
        link: '/valuation',
        external: false
      },
      secondaryCTA: {
        text: 'Book Expert Review',
        link: appConfig.links.calendly,
        external: true
      }
    },

    // After process sections - focus on starting the journey
    process: {
      badge: 'Begin Your Exit',
      title: "Let's Start Your Success Story",
      description: 'Join hundreds of business owners who trusted us to maximize their exit value.',
      primaryCTA: {
        text: 'Get Your Valuation',
        link: '/valuation',
        external: false
      },
      secondaryCTA: {
        text: 'Call Us Directly',
        link: `tel:${appConfig.site.contactPhone}`,
        external: false,
        phone: true
      }
    },

    // After success stories - focus on emulation
    stories: {
      badge: 'Your Turn',
      title: 'Ready to Write Your Own Success Story?',
      description: 'See what your business could be worth and explore your exit options.',
      primaryCTA: {
        text: 'Get Free Valuation',
        link: '/valuation',
        external: false
      },
      secondaryCTA: {
        text: 'Discuss Your Goals',
        link: appConfig.links.calendly,
        external: true
      }
    },

    // After FAQ sections - address remaining concerns
    faq: {
      badge: 'Still Have Questions?',
      title: "Let's Talk About Your Specific Situation",
      description: 'Every business is unique. Get personalized answers from our team.',
      primaryCTA: {
        text: 'Schedule a Call',
        link: appConfig.links.calendly,
        external: true
      },
      secondaryCTA: {
        text: 'Start Valuation',
        link: '/valuation',
        external: false
      }
    },

    // After platform/technology sections - focus on experiencing it
    platform: {
      badge: 'Experience AMIVA+',
      title: 'See Our Platform in Action',
      description: 'Start with a free valuation and experience the transparency that sets us apart.',
      primaryCTA: {
        text: 'Try Free Valuation',
        link: '/valuation',
        external: false
      },
      secondaryCTA: {
        text: 'Request Demo',
        link: appConfig.links.calendly,
        external: true
      }
    },

    // Default variant - balanced approach
    default: {
      badge: 'Get Started',
      title: 'Ready to Explore Your Options?',
      description: 'Get a confidential discussion about your business\'s true value and exit opportunities.',
      primaryCTA: {
        text: 'Book Free Consultation',
        link: appConfig.links.calendly,
        external: true
      },
      secondaryCTA: {
        text: 'Get Free Valuation',
        link: '/valuation',
        external: false
      }
    }
  };

  const content = variants[variant] || variants.default;

  return (
    <div className={`bg-gradient-to-br from-gray-900 to-black py-16 lg:py-20 ${className}`}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Badge */}
        <div className="inline-flex items-center justify-center mb-6">
          <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
            <div className="flex items-center space-x-2">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amiva-accent opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-amiva-accent"></span>
              </span>
              <span className="text-white text-sm font-medium">{content.badge}</span>
            </div>
          </div>
        </div>

        {/* Title */}
        <h2 className="text-3xl lg:text-5xl font-bold text-white mb-4 lg:mb-6">
          {content.title}
        </h2>

        {/* Description */}
        <p className="text-lg lg:text-xl text-gray-300 mb-8 lg:mb-10 leading-relaxed max-w-2xl mx-auto">
          {content.description}
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          {content.primaryCTA.external ? (
            <a
              href={content.primaryCTA.link}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto bg-amiva-gradient text-white px-8 lg:px-10 py-4 rounded-2xl font-semibold text-base lg:text-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
            >
              {content.primaryCTA.text}
            </a>
          ) : (
            <Link
              to={content.primaryCTA.link}
              className="w-full sm:w-auto bg-amiva-gradient text-white px-8 lg:px-10 py-4 rounded-2xl font-semibold text-base lg:text-lg hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
            >
              {content.primaryCTA.text}
            </Link>
          )}

          {content.secondaryCTA.phone ? (
            <a
              href={content.secondaryCTA.link}
              className="w-full sm:w-auto bg-transparent text-white border-2 border-white/30 px-8 lg:px-10 py-4 rounded-2xl font-semibold text-base lg:text-lg hover:bg-white/10 hover:border-white/50 transition-all duration-300"
            >
              {content.secondaryCTA.text}
            </a>
          ) : content.secondaryCTA.external ? (
            <a
              href={content.secondaryCTA.link}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto bg-transparent text-white border-2 border-white/30 px-8 lg:px-10 py-4 rounded-2xl font-semibold text-base lg:text-lg hover:bg-white/10 hover:border-white/50 transition-all duration-300"
            >
              {content.secondaryCTA.text}
            </a>
          ) : (
            <Link
              to={content.secondaryCTA.link}
              className="w-full sm:w-auto bg-transparent text-white border-2 border-white/30 px-8 lg:px-10 py-4 rounded-2xl font-semibold text-base lg:text-lg hover:bg-white/10 hover:border-white/50 transition-all duration-300"
            >
              {content.secondaryCTA.text}
            </Link>
          )}
        </div>

        {/* Trust Indicators */}
        <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-6 text-sm text-gray-400">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span>100% Confidential</span>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span>No Obligations</span>
          </div>
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clipRule="evenodd"
              />
            </svg>
            <span>Expert Guidance</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContextualCTA;




